import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  ArrowForward,
  ErrorOutline,
  VisibilityOffOutlined,
  WarningAmber,
} from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';
import type { InspectionAlert } from '@/helpers/inspectionAlerts';

const alertStyleMap = {
  OVERDUE: {
    icon: ErrorOutline,
    iconColor: 'error.main',
    borderColor: (theme: Theme) => alpha(theme.palette.error.main, 0.35),
    background: (theme: Theme) => alpha(theme.palette.error.main, 0.05),
    titleColor: 'error.dark',
  },
  DUE_SOON: {
    icon: WarningAmber,
    iconColor: 'warning.dark',
    borderColor: (theme: Theme) => alpha(theme.palette.warning.main, 0.35),
    background: (theme: Theme) => alpha(theme.palette.warning.main, 0.08),
    titleColor: 'warning.dark',
  },
} as const;

interface InspectionAlertCardProps {
  alert: InspectionAlert;
  showBuildingName?: boolean;
  onDismiss?: (alert: InspectionAlert) => void;
  dismissDisabled?: boolean;
}

const InspectionAlertCard = ({
  alert,
  showBuildingName = false,
  onDismiss,
  dismissDisabled = false,
}: InspectionAlertCardProps) => {
  const styles = alertStyleMap[alert.alertType];
  const Icon = styles.icon;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: styles.borderColor,
        background: styles.background,
        borderRadius: 3,
        p: 1.5,
        '@media (min-width: 1800px)': { p: 2 },
        height: '100%',
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={{ xs: 1.25, sm: 1 }} alignItems="flex-start" sx={{ height: '100%' }}>
        <Icon
          sx={{
            color: styles.iconColor,
            mt: 0.25,
            fontSize: { xs: 22, sm: 20 },
            '@media (min-width: 1800px)': { fontSize: 24 },
          }}
        />
        <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minWidth: 0, height: '100%' }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Typography
              variant="subtitle2"
              sx={{
                color: styles.titleColor,
                fontWeight: 700,
                fontSize: { xs: '0.875rem', sm: '0.75rem' },
                pr: 1,
                overflowWrap: 'break-word',
                '@media (min-width: 1800px)': { fontSize: '0.875rem' },
              }}
            >
              {alert.title}
            </Typography>
            {onDismiss && (
              <IconButton
                size="small"
                disabled={dismissDisabled}
                onClick={() => onDismiss(alert)}
                sx={{ mt: -0.5, mr: -0.75 }}
                title="Skrýt do zítřka"
              >
                <VisibilityOffOutlined fontSize="small" />
              </IconButton>
            )}
          </Stack>

          {showBuildingName && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              Objekt: {alert.buildingName}
            </Typography>
          )}

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1.25,
              fontSize: { sm: '0.75rem' },
              overflowWrap: 'break-word',
              '@media (min-width: 1800px)': { fontSize: '0.875rem' },
            }}
          >
            {alert.message}
          </Typography>

          <Button
            component={RouterLink}
            to={`${alert.actionPath}?section=inspections`}
            size="small"
            endIcon={<ArrowForward fontSize="small" />}
            sx={{
              px: 0,
              mt: 'auto',
              minWidth: 0,
              alignSelf: 'flex-start',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { sm: '0.75rem' },
              textAlign: 'left',
              '@media (min-width: 1800px)': { fontSize: '0.8125rem' },
            }}
          >
            Zobrazit revizi a dokumenty
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default InspectionAlertCard;
